import { createRouter, createWebHistory } from '@ionic/vue-router';
import { RouteRecordRaw } from 'vue-router';

import matomoHelper from '@/helper/matomo';
import { getPrivacyPageId } from '@/service';
import { useAuthStore } from '@/stores/auth';
import { useStoriesStore } from '@/stores/stories';
import AuthWrapper from '@/views/AuthWrapper.vue';
import BaseWrapper from '@/views/BaseWrapper.vue';

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    component: BaseWrapper,
    children: [
      {
        path: '',
        redirect: '/start',
      },
      {
        path: 'start',
        name: 'home',
        meta: { title: 'Startseite' },
        component: () => import('@/views/Home/HomePage.vue'),
      },
      {
        path: 'stories',
        name: 'stories',
        meta: { title: 'Neuigkeiten' },
        component: () => import('@/views/Home/HomePage.vue'),
      },
      {
        path: 'news',
        name: 'news',
        meta: { title: 'News' },
        component: () => import('@/views/News/NewsFeed.vue'),
      },
      {
        path: 'news/:id',
        name: 'news-detail',
        component: () => import('@/views/News/NewsDetail.vue'),
      },
      {
        path: 'notes',
        name: 'notes',
        meta: { requiresAuth: true, title: 'Hinweise' },
        component: () => import('@/views/Notes/NotesFeed.vue'),
      },
      {
        path: 'notes/:id',
        name: 'notes-detail',
        meta: { requiresAuth: true },
        component: () => import('@/views/Notes/NotesDetail.vue'),
      },
      {
        path: 'events',
        name: 'events',
        meta: { title: 'Events' },
        component: () => import('@/views/Events/EventsFeed.vue'),
      },
      {
        path: 'events/:id',
        name: 'events-detail',
        component: () => import('@/views/Events/EventsDetail.vue'),
      },
      {
        path: 'pages',
        name: 'pages',
        meta: { title: 'Seitenübersicht' },
        component: () => import('@/views/Pages/PagesMenu.vue'),
      },
      {
        path: 'pages/:id',
        name: 'pages-detail',
        component: () => import('@/views/Pages/ContentPage.vue'),
      },
      {
        path: 'classifieds',
        name: 'classifieds',
        meta: { requiresAuth: true, title: 'Kleinanzeigen' },
        component: () => import('@/views/Classifieds/ClassifiedsFeed.vue'),
      },
      {
        path: 'search/:query',
        name: 'search',
        meta: { title: 'Suchergebnisse' },
        component: () => import('@/views/Search/SearchResults.vue'),
      },
      {
        path: 'profile',
        name: 'profile',
        meta: { requiresAuth: true, title: 'Profil' },
        component: () => import('@/views/Profile/ProfilePage.vue'),
      },
      {
        path: 'legal/privacy-policy',
        name: 'privacy-policy-redirect',
        beforeEnter: async (_to, _from, next) => {
          try {
            const response = await getPrivacyPageId();
            const pageId = (response.data as { page_id?: number })?.page_id;
            if (pageId) {
              next({ path: `/pages/${pageId}` });
              return;
            }
          } catch (error) {
            console.error('Failed to fetch privacy page ID:', error);
          }
          // Fallback: redirect to home if API fails
          next({ name: 'home' });
        },
        component: () => import('@/views/Home/HomePage.vue'), // Placeholder, beforeEnter redirects
      },
    ],
  },
  {
    path: '/auth',
    component: AuthWrapper,
    children: [
      {
        path: '',
        name: 'entra-login',
        meta: { requiresPublic: true, title: 'Login' },
        component: () => import('@/views/Auth/AuthLogin.vue'),
      },
    ],
  },
  {
    path: '/:catchAll(.*)',
    redirect: '/',
  },
];

export const router = createRouter({
  history: createWebHistory('/'),
  routes,
});

router.beforeEach((to: any, from: any, next: any) => {
  const authStore = useAuthStore();
  const storiesStore = useStoriesStore();
  
  // Only close overlay if we're not navigating to /stories route
  if (to.name !== 'stories') {
    storiesStore.overlayOpen = false;
  }

  if (to.meta.requiresAuth && !authStore.user?.id) {
    // Store the intended destination so we can redirect back after login
    authStore.setReturnUrl(to.fullPath);
    next({ name: 'entra-login' });
    return;
  }

  if (to.meta.requiresPublic && authStore.user?.id) {
    // Don't redirect if we're coming from a protected route (likely a reload scenario)
    // Let the AuthLogin component handle the redirect to preserve logged-in=true parameter
    if (from.meta?.requiresAuth) {
      next();
      return;
    }
    next({ name: 'home' });
    return;
  }

  next();
});

router.afterEach((to: any) => {
  if (to.meta && to.meta.title) {
    document.title = to.meta.title + ' - Uniklinik Köln App';
    matomoHelper.trackPageView(to.meta.title);
    return;
  }
  document.title = `Uniklinik Köln App`;
});

export default router;
